<template>
  <div class="chat-container">
    <div class="chat-sidebar">
      <div class="chat-sidebar-header">
        <h3>Conversations</h3>
        <div class="sidebar-buttons">
          <button class="btn-clear" @click="clearAllSessions" v-if="sessions.length > 0" title="Clear all">Clear</button>
          <button class="btn-new-chat" @click="startNewChat">+ New</button>
        </div>
      </div>
      <div class="chat-session-list">
        <div
          v-for="session in sessions"
          :key="session.id"
          class="chat-session-item"
          :class="{ active: session.id === activeSessionId }"
          @click="loadSession(session.id)"
        >
          <div class="session-row">
            <div class="session-text">
              <div class="session-title">{{ session.title || 'New conversation' }}</div>
              <div class="session-date">{{ formatDate(session.updatedAt) }}</div>
            </div>
            <button class="btn-delete-session" @click.stop="deleteSession(session.id)" title="Delete">x</button>
          </div>
        </div>
        <div v-if="sessions.length === 0" class="no-sessions">
          No conversations yet
        </div>
      </div>
    </div>

    <div class="chat-main">
      <div class="chat-messages" ref="messagesContainer">
        <div
          v-for="(msg, i) in messages"
          :key="i"
          class="chat-message"
          :class="'message-' + msg.role"
        >
          <div v-if="msg.role === 'user'" class="message-bubble user-bubble">
            {{ msg.content }}
          </div>
          <div v-else-if="msg.role === 'assistant' && msg.content" class="message-bubble assistant-bubble markdown-body" v-html="renderMarkdown(msg.content)">
          </div>
          <div v-if="msg.toolCalls && msg.toolCalls.length" class="tool-call-chips">
            <template v-if="msg.toolCalls.length <= 5">
              <div
                v-for="tc in msg.toolCalls"
                :key="tc.id"
                class="tool-call-chip"
                :class="{ 'tool-error': tc._error, 'tool-ok': !tc._error }"
              >
                <span class="tool-icon">{{ tc._error ? '⚠' : '✓' }}</span>
                {{ tc._label || tc.name }}
              </div>
            </template>
            <template v-else>
              <div class="tool-call-summary" @click="toggleToolCallExpansion(i)">
                <span class="tool-icon">⚙</span>
                {{ msg.toolCalls.filter(t => !t._error).length }} ok, {{ msg.toolCalls.filter(t => t._error).length }} errors ({{ msg.toolCalls.length }} tool calls)
                <span class="expand-toggle">{{ expandedToolCalls.has(i) ? 'hide' : 'show' }}</span>
              </div>
              <template v-if="expandedToolCalls.has(i)">
                <div
                  v-for="tc in msg.toolCalls"
                  :key="tc.id"
                  class="tool-call-chip"
                  :class="{ 'tool-error': tc._error, 'tool-ok': !tc._error }"
                >
                  <span class="tool-icon">{{ tc._error ? '⚠' : '✓' }}</span>
                  {{ tc._label || tc.name }}
                </div>
              </template>
            </template>
          </div>
        </div>

        <div v-if="streaming" class="chat-message message-assistant">
          <div class="message-bubble assistant-bubble streaming-bubble markdown-body">
            <span v-if="streamingContent" v-html="renderMarkdown(streamingContent)"></span>
            <span v-else class="typing-indicator">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </span>
          </div>
          <div v-if="activeToolCalls.length" class="tool-call-chips">
            <div v-for="tc in activeToolCalls" :key="tc.id" class="tool-call-chip tool-pending">
              <span class="tool-spinner"></span>
              {{ tc.label }}
            </div>
          </div>
        </div>
      </div>

      <div class="chat-input-area">
        <textarea
          ref="chatInput"
          v-model="inputText"
          class="chat-input"
          placeholder="Ask about tenant security configuration..."
          @keydown.enter.exact.prevent="sendMessage"
          :disabled="streaming"
          rows="2"
        ></textarea>
        <button class="btn-send" @click="sendMessage" :disabled="streaming || !inputText.trim()">
          Send
        </button>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-dialog">
        <div class="modal-header">
          <h4 class="modal-title">{{ modalTitle }}</h4>
          <button class="modal-close" @click="closeModal">x</button>
        </div>
        <div class="modal-body">
          <p class="modal-message">{{ modalMessage }}</p>
        </div>
        <div class="modal-footer">
          <button class="modal-btn modal-btn-cancel" @click="closeModal">Cancel</button>
          <button class="modal-btn modal-btn-confirm" @click="modalConfirm">{{ modalConfirmLabel }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { streamChatMessage, listChatSessions, getChatSessionMessages, deleteChatSession, deleteAllChatSessions } from '@/services/chat-api.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

marked.setOptions({
  breaks: true,
  gfm: true
});

export default {
  name: 'CyanicAssistantChat',
  data() {
    return {
      sessions: [],
      activeSessionId: null,
      messages: [],
      inputText: '',
      streaming: false,
      streamingContent: '',
      activeToolCalls: [],
      showModal: false,
      modalTitle: '',
      modalMessage: '',
      modalConfirmLabel: 'Confirm',
      modalAction: null,
      expandedToolCalls: new Set()
    };
  },
  async mounted() {
    await this.loadSessions();
    this.$nextTick(() => this.$refs.chatInput?.focus());
  },
  methods: {
    async loadSessions() {
      try {
        this.sessions = await listChatSessions();
      } catch (e) {
        console.error('Failed to load sessions:', e);
      }
    },
    async loadSession(sessionId) {
      this.activeSessionId = sessionId;
      try {
        const data = await getChatSessionMessages(sessionId);
        const chipsByMessage = {};
        (data.toolCalls || []).forEach(tc => {
          if (!chipsByMessage[tc.messageId]) chipsByMessage[tc.messageId] = [];
          chipsByMessage[tc.messageId].push({
            id: tc.id,
            name: tc.toolName,
            _label: tc.resultSummary || tc.toolName,
            _error: !tc.ok
          });
        });
        const grouped = [];
        (data.messages || []).forEach(m => {
          if (m.role === 'tool') return;
          if (m.role === 'user') {
            grouped.push({ role: 'user', content: m.content, toolCalls: [] });
            return;
          }
          const chips = chipsByMessage[m.id] || [];
          const last = grouped[grouped.length - 1];
          if (last && last.role === 'assistant') {
            if (m.content) last.content = last.content ? last.content + '\n\n' + m.content : m.content;
            last.toolCalls = last.toolCalls.concat(chips);
          } else {
            grouped.push({ role: 'assistant', content: m.content || '', toolCalls: chips });
          }
        });
        this.messages = grouped;
        this.$nextTick(() => this.scrollToBottom());
      } catch (e) {
        console.error('Failed to load session:', e);
      }
    },
    startNewChat() {
      this.activeSessionId = null;
      this.messages = [];
      this.$nextTick(() => this.$refs.chatInput?.focus());
    },
    async deleteSession(sessionId) {
      this.openModal('Delete conversation', 'Delete this conversation? This cannot be undone.', 'Delete', async () => {
        try {
          await deleteChatSession(sessionId);
          if (this.activeSessionId === sessionId) {
            this.activeSessionId = null;
            this.messages = [];
          }
          await this.loadSessions();
        } catch (e) {
          console.error('Failed to delete session:', e);
        }
      });
    },
    async clearAllSessions() {
      this.openModal('Clear all conversations', 'Delete all conversations? This cannot be undone.', 'Delete all', async () => {
        try {
          await deleteAllChatSessions();
          this.activeSessionId = null;
          this.messages = [];
          await this.loadSessions();
        } catch (e) {
          console.error('Failed to clear sessions:', e);
        }
      });
    },
    openModal(title, message, confirmLabel, action) {
      this.modalTitle = title;
      this.modalMessage = message;
      this.modalConfirmLabel = confirmLabel;
      this.modalAction = action;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.modalAction = null;
    },
    modalConfirm() {
      const action = this.modalAction;
      this.closeModal();
      if (action) action();
    },
    async sendMessage() {
      const text = this.inputText.trim();
      if (!text || this.streaming) return;

      this.messages.push({ role: 'user', content: text });
      this.inputText = '';
      this.streaming = true;
      this.streamingContent = '';
      this.activeToolCalls = [];

      const assistantMsg = { role: 'assistant', content: '', toolCalls: [] };
      this.messages.push(assistantMsg);

      try {
        await streamChatMessage(text, this.activeSessionId, {
          onSession: (data) => {
            this.activeSessionId = data.sessionId;
          },
          onToken: (tokenText) => {
            this.streamingContent += tokenText;
            assistantMsg.content = this.streamingContent;
        this.$nextTick(() => {
          this.scrollToBottom();
          this.$refs.chatInput?.focus();
        });
          },
          onToolCall: (data) => {
            if (this.streamingContent && !this.streamingContent.endsWith('\n\n')) {
              this.streamingContent += '\n\n';
              assistantMsg.content = this.streamingContent;
            }
            this.activeToolCalls.push({
              id: data.id,
              label: data.name
            });
          },
          onToolResult: (data) => {
            const idx = this.activeToolCalls.findIndex(tc => tc.id === data.id);
            if (idx !== -1) this.activeToolCalls.splice(idx, 1);
            assistantMsg.toolCalls.push({
              id: data.id,
              name: data.name,
              _label: data.summary,
              _error: !data.ok
            });
          },
          onDone: (data) => {
            if (data && data.error) {
              assistantMsg.content = (assistantMsg.content ? assistantMsg.content + '\n\n' : '') + `<p><strong>Error:</strong> ${data.error}</p>`;
            }
            this.streaming = false;
            this.activeToolCalls = [];
            this.loadSessions();
            this.$nextTick(() => this.$refs.chatInput?.focus());
          },
          onError: (data) => {
            assistantMsg.content = `<p><strong>Error:</strong> ${data.message}</p>`;
            this.streaming = false;
            this.activeToolCalls = [];
            this.$nextTick(() => this.$refs.chatInput?.focus());
          }
        });
      } catch (e) {
        assistantMsg.content = `<p><strong>Error:</strong> ${e.message}</p>`;
        this.streaming = false;
        this.activeToolCalls = [];
        this.$nextTick(() => this.$refs.chatInput?.focus());
      }
    },
    renderMarkdown(text) {
      if (!text) return '';
      return DOMPurify.sanitize(marked.parse(text));
    },
    toggleToolCallExpansion(msgIndex) {
      if (this.expandedToolCalls.has(msgIndex)) {
        this.expandedToolCalls.delete(msgIndex);
      } else {
        this.expandedToolCalls.add(msgIndex);
      }
      this.expandedToolCalls = new Set(this.expandedToolCalls);
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) container.scrollTop = container.scrollHeight;
    },
    formatDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  }
};
</script>

<style scoped>
.chat-container {
  display: flex;
  height: calc(100vh - 60px);
  font-family: 'Inter', -apple-system, sans-serif;
}

.chat-sidebar {
  width: 260px;
  border-right: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.chat-sidebar-header {
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
}

.chat-sidebar-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.btn-new-chat {
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
}

.btn-new-chat:hover {
  background: #1d4ed8;
}

.sidebar-buttons {
  display: flex;
  gap: 6px;
}

.btn-clear {
  background: transparent;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 12px;
  cursor: pointer;
}

.btn-clear:hover {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fca5a5;
}

.session-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.session-text {
  flex: 1;
  min-width: 0;
}

.btn-delete-session {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s;
}

.chat-session-item:hover .btn-delete-session {
  opacity: 1;
}

.btn-delete-session:hover {
  color: #dc2626;
}

.chat-session-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.chat-session-item {
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 4px;
}

.chat-session-item:hover {
  background: #f3f4f6;
}

.chat-session-item.active {
  background: #e0e7ff;
}

.session-title {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.session-date {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.no-sessions {
  padding: 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}

.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.chat-message {
  margin-bottom: 16px;
  max-width: 80%;
}

.message-user {
  margin-left: auto;
}

.message-bubble {
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
}

.user-bubble {
  background: #2563eb;
  color: white;
}

.assistant-bubble {
  background: #f3f4f6;
  color: #111827;
}

.assistant-bubble :deep(h2) {
  font-size: 16px;
  margin: 0 0 8px;
}

.assistant-bubble :deep(h3) {
  font-size: 14px;
  margin: 12px 0 6px;
}

.assistant-bubble :deep(p) {
  margin: 0 0 8px;
}

.assistant-bubble :deep(ul) {
  margin: 0 0 8px;
  padding-left: 20px;
}

.assistant-bubble :deep(code) {
  background: #e5e7eb;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 13px;
}

.assistant-bubble :deep(strong) {
  font-weight: 600;
}

.markdown-body :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
  font-size: 13px;
}

.markdown-body :deep(th) {
  background: #e5e7eb;
  font-weight: 600;
  text-align: left;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  white-space: nowrap;
}

.markdown-body :deep(td) {
  padding: 5px 10px;
  border: 1px solid #e5e7eb;
}

.markdown-body :deep(tr:nth-child(even)) {
  background: #fafafa;
}

.markdown-body :deep(ol) {
  margin: 4px 0 8px;
  padding-left: 20px;
}

.markdown-body :deep(li) {
  margin-bottom: 2px;
}

.markdown-body :deep(hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 12px 0;
}

.markdown-body :deep(blockquote) {
  border-left: 3px solid #d1d5db;
  margin: 8px 0;
  padding: 4px 12px;
  color: #6b7280;
}

.streaming-bubble {
  min-height: 24px;
}

.typing-indicator {
  display: inline-flex;
  gap: 4px;
  align-items: center;
}

.typing-indicator .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9ca3af;
  animation: bounce 1.4s infinite;
}

.typing-indicator .dot:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator .dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

.tool-call-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.tool-call-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.tool-ok {
  background: #d1fae5;
  color: #065f46;
}

.tool-call-summary {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: #f3f4f6;
  color: #4b5563;
  cursor: pointer;
  user-select: none;
}

.tool-call-summary:hover {
  background: #e5e7eb;
}

.expand-toggle {
  color: #2563eb;
  font-weight: 600;
}

.tool-error {
  background: #fee2e2;
  color: #991b1b;
}

.tool-pending {
  background: #fef3c7;
  color: #92400e;
}

.tool-spinner {
  width: 10px;
  height: 10px;
  border: 2px solid #fbbf24;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.chat-input-area {
  display: flex;
  gap: 8px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: white;
}

.chat-input {
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  outline: none;
}

.chat-input:focus {
  border-color: #2563eb;
}

.btn-send {
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-send:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-send:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 12px;
  width: 400px;
  max-width: 90vw;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
}

.modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.modal-close {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.modal-close:hover {
  color: #4b5563;
}

.modal-body {
  padding: 20px;
}

.modal-message {
  margin: 0;
  font-size: 14px;
  color: #4b5563;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #f3f4f6;
}

.modal-btn {
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.modal-btn-cancel {
  background: white;
  color: #4b5563;
  border: 1px solid #d1d5db;
}

.modal-btn-cancel:hover {
  background: #f9fafb;
}

.modal-btn-confirm {
  background: #dc2626;
  color: white;
}

.modal-btn-confirm:hover {
  background: #b91c1c;
}
</style>
